/**
 * S18. Metered usage and plan. Two scopes, one screen.
 *
 * IT IS ALWAYS REACHABLE, WITH OR WITHOUT A PROJECT. `/usage` is the
 * account roll-up and `/p/:slug/usage` is one project; the account form
 * renders for somebody who has never connected anything, because "what have
 * I used, and against what ceiling" is a question a person is entitled to an
 * answer to before they own an app, not after. A zero roll-up on a plan with
 * real limits is a true and useful reading; a screen that 404s until you own
 * a project is neither.
 *
 * COMPUTE, NOT REQUESTS (2026-09-06). Simple's first meter is one abstract
 * figure with the request count and the processing time folded into it
 * (apps/cloud/src/services/usage/compute.ts holds the multiples, and holds
 * them once). The mental model is an assistant's quota: one allowance, a
 * percentage of it spent, the arithmetic out of sight. The Advanced layer
 * that printed both raw inputs, and the mock-only day by day table under it,
 * were cut on 2026-09-06: the screen is the bars and the policy block now.
 * Git history has the markup if the raw figures are wanted back.
 *
 * THE PERCENTAGE CARRIES TWO DECIMALS. A month's compute allowance is
 * hundreds of thousands of units, so a whole percent is a bracket thousands
 * of units wide, and an app that has genuinely done work reading "0%" is the
 * screen telling a person their usage is not being counted. Two places is the
 * smallest number that keeps the figure moving as the product is used.
 *
 * A trust screen, and the argument for building it is one sentence: honest
 * limits, stated before they bite, read as an honest company. Every number on
 * this page is one a person could otherwise only discover by hitting it.
 *
 * THE POLICY BLOCK IS THE POINT OF THE SCREEN, not the bars. "If you reach a
 * limit, your app keeps running." is published in advance, in Simple copy, in
 * a flat block that is always visible whatever the numbers say. (Until
 * 2026-10-13 the over-limit line also said new requests were "slowed, not
 * blocked", which nothing ever did; it now says what happens: compute past
 * the allowance is paid from credits, and nothing is limited.) Nothing builds
 * trust like publishing the failure mode, and nothing destroys it like a
 * person learning the policy from the outage.
 *
 * THE BAR IS DECORATION, THE NUMBER IS THE CONTENT. That rule lives in the
 * usage bar component; this screen keeps it by putting the percentage in its
 * own column beside the label rather than inside the track, so at 1.4x string
 * expansion the label wraps and the track shrinks and the number never moves.
 *
 * ZERO IS A REAL STATE. Soi Cats has never had a visitor, so its compute bar
 * and its database bar read "No traffic yet" rather than sitting at zero
 * looking broken. A new project has not failed at anything, and neither has
 * an account with no projects in it.
 *
 * Its files bar is not one of them, and the reason is worth keeping. Soi Cats
 * arrived with three files synced out of its repository, so its file storage
 * genuinely holds bytes, and the seed now sums them from the file rows
 * themselves. Writing "all three at zero" here was how the fixture came to
 * claim two contradictory things about one number: this page said no bytes,
 * and the add-ons card a click away said three files. A project can have
 * storage and no visitors at the same time, and the page should say so.
 *
 * THE FOUR THRESHOLDS, and where each one is in the seeded narrative:
 *   comfortable   Herb and Ash, compute at 42 percent
 *   approaching   Studio Ledger, database at 82 percent
 *   no traffic    Soi Cats, compute and database at zero, files at 77.5 KB
 *   at limit      ?state=at-limit, on any project
 *   over          ?state=over, which draws the hatch past the end of the track
 * The last two are forced rather than seeded because no project in the
 * narrative is over its limit, and inventing one would contradict every other
 * screen that says those projects are fine.
 *
 * SIMPLE VERSUS ADVANCED. Advanced appends the day by day table, the exact
 * numbers, the plan's own limits as a definition list, and the two meters the
 * vocabulary contract marks Advanced only: processing time and sub-requests.
 * The one currency figure is the credit balance in the plan panel
 * (components/patterns/PlanSummary.tsx), a balance rather than a bill: the
 * allowance is per account, and credits will pay for usage past it once
 * pricing exists. Nothing is charged or limited yet.
 */

import { useEffect } from 'react';
import { Link } from 'react-router';

import { Button } from '../../components/primitives/Button';

import { EmptyState } from '../../components/patterns/EmptyState';
import { Notice } from '../../components/patterns/Notice';
import { PlanSummary } from '../../components/patterns/PlanSummary';
import { SectionPanel } from '../../components/patterns/SectionPanel';
import { Skeleton } from '../../components/patterns/Skeleton';
import { UsageBar } from '../../components/patterns/UsageBar';
import { daysUntil } from '../../components/patterns/UsageStrip';

import { environmentOf, getProject, getUsage } from '../../lib/api';
import { useProjects } from '../../lib/auth/use-app-data';
import { percent, percentLabel } from '../../lib/format';
import { useAsync } from '../../lib/hooks/use-async';
import { useProjectSlug } from '../../lib/hooks/use-project-slug';
import { useScreenState } from '../../lib/hooks/use-screen-state';
import { t } from '../../lib/i18n/t';
import { useDemoStore } from '../../lib/mock/store';
import { captureEvent } from '../../lib/analytics/posthog';
import type { UsageMetric } from '../../lib/types';

import '../../styles/components/usage.css';

const STATES = ['resting', 'at-limit', 'over', 'none', 'loading'] as const;
type StateName = (typeof STATES)[number];

/** The overage the spec's own example uses, so the hatch state reads the way
 *  it was written: "Over the limit by 12 MB". */
const OVER_BY = 12 * 1024 * 1024;

/**
 * The forced thresholds. They move the number and let every derived thing
 * follow, rather than setting a tone directly: a state that painted the bar
 * red without changing the number would be a screenshot of a lie.
 */
function forceState(metrics: UsageMetric[], state: StateName): UsageMetric[] {
  if (state === 'none') return metrics.map((metric) => ({ ...metric, used: 0, state: 'none' }));

  if (state === 'at-limit' || state === 'over') {
    let done = false;
    return metrics.map((metric) => {
      if (done || metric.resource !== 'database-storage') return metric;
      done = true;
      return {
        ...metric,
        used: state === 'over' ? metric.limit + OVER_BY : metric.limit,
        state: 'over',
      };
    });
  }

  return metrics;
}

/* ------------------------------------------------------------------- rows */

interface RowProps {
  metric: UsageMetric;
  /** Where the resource can be looked at. Only two of the three have a place. */
  to: string | null;
  /** True when this row may spend the screen's one ink block. */
  ink: boolean;
  /** See UsageBar's showLimitAtZero: an account with no apps is shown its ceiling. */
  limitAtZero: boolean;
}

function tone(metric: UsageMetric): 'none' | 'ok' | 'near' | 'over' | 'at' {
  const pct = percent(metric.used, metric.limit);
  if (metric.used <= 0) return 'none';
  if (metric.used > metric.limit) return 'over';
  if (pct >= 100) return 'at';
  if (pct >= 80) return 'near';
  return 'ok';
}

function UsageRow({ metric, to, ink, limitAtZero }: RowProps) {
  const shade = tone(metric);

  return (
    <div className="uz__row" data-tone={shade}>
      <div className="uz__bar">
        <UsageBar
          label={t(metric.label)}
          used={metric.used}
          limit={metric.limit}
          unit={metric.unit}
          showLimitAtZero={limitAtZero}
          // Compute's amount is dropped here rather than doubled: the
          // percentage column to the right of this bar is already the
          // reading, and it is aligned across all three rows.
          percentOnly={metric.unit === 'count'}
        />
      </div>

      {/* The percentage is a reading of the number beside it, so it is quiet
          and it never carries the state on its own. Two decimals, because a
          monthly allowance is large enough that a whole percent is a bracket
          thousands of units wide (see the file header). */}
      <span className="uz__pct mono">
        {shade === 'none' ? '' : t('{n}%', { n: percentLabel(metric.used, metric.limit) })}
      </span>

      {shade === 'near' || shade === 'at' || shade === 'over' ? (
        <p className="uz__flag" data-tone={shade}>
          <span className="uz__flag-glyph" aria-hidden="true">
            <svg viewBox="0 0 16 16" width="12" height="12" focusable="false">
              <path
                d="M8 2.2 15 14H1Z"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.4"
                strokeLinejoin="round"
              />
              <path d="M8 6.6v3.2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
              <circle cx="8" cy="11.9" r="0.9" fill="currentColor" />
            </svg>
          </span>
          <span className="uz__flag-say">
            {shade === 'near'
              ? t('Close to the limit')
              : shade === 'at'
                ? t('At the limit')
                : metric.unit === 'count'
                  ? t('Past your allowance. Extra use is paid from your credits, and nothing is limited.')
                  : t('Over the limit. Storage past the allowance is not charged yet, and nothing is limited.')}
          </span>
        </p>
      ) : null}

      {to ? (
        <p className="uz__foot">
          {ink ? (
            <Link to={to}>
              <Button variant="solid">{t("See what's taking space")}</Button>
            </Link>
          ) : (
            <Link to={to} className="link-inline">
              {t("See what's taking space")}
            </Link>
          )}
        </p>
      ) : null}
    </div>
  );
}

/* ----------------------------------------------------------------- screen */

export default function Usage() {
  const slug = useProjectSlug();
  const scenarioId = useDemoStore((s) => s.scenarioId);
  const { state } = useScreenState<StateName>(STATES, 'resting');

  // Account scope is the no-slug form of this screen, not a second screen.
  // It answers the same question against the plan's whole allowance, and it
  // has to answer it for somebody who owns nothing yet.
  const accountScope = slug === null;

  const cachedProjects = useProjects();
  const cachedProject = slug ? cachedProjects.find((p) => p.slug === slug) : undefined;
  const { data: fetchedProject, isLoading: isProjectLoading, error: projectError } = useAsync(
    () => (slug ? getProject(slug) : Promise.resolve(null)),
    [slug, scenarioId],
  );
  const project = fetchedProject ?? cachedProject;
  const projectId = project?.id ?? '';

  // The roll-up needs no project, and asking for it before one resolves is
  // what makes the account form render for an account with none.
  const { data: usage, error: usageError } = useAsync(
    () =>
      accountScope
        ? getUsage(null)
        : projectId
          ? getUsage(projectId)
          : Promise.resolve(null),
    [accountScope, projectId, scenarioId],
  );

  useEffect(() => {
    if (usage && (accountScope || project)) {
      captureEvent('usage_screen_viewed', {
        scope: accountScope ? 'account' : 'project',
        project_slug: project?.slug ?? null,
        project_count: accountScope ? cachedProjects.length : null,
        plan: usage.plan,
        plan_expires: usage.planExpiresAt !== null,
        has_credits: usage.credits
          ? Number(usage.credits.paidUsd) + Number(usage.credits.promotionalUsd) > 0
          : null,
        resets_in_days: daysUntil(usage.resetsAt),
      });
    }
  }, [accountScope, cachedProjects.length, project, usage]);

  if ((!accountScope && isProjectLoading && !project) || !usage || state === 'loading') {
    return (
      <div className="page">
        <header className="page__head">
          <h1 className="page__title">{t('Usage')}</h1>
        </header>
        <Skeleton shape="track" count={3} label={t('Working out your usage')} />
      </div>
    );
  }

  if (!accountScope && (projectError || !project)) {
    return (
      <div className="page">
        <header className="page__head">
          <h1 className="page__title">{t('Usage')}</h1>
        </header>
        <Notice tone="quiet" title={t('That project is not here.')}>
          <p>{t('It may have been deleted, or the address may have a typo in it.')}</p>
        </Notice>
      </div>
    );
  }

  if (usageError) {
    return (
      <div className="page">
        <header className="page__head">
          <h1 className="page__title">{t('Usage')}</h1>
        </header>
        <Notice tone="quiet" title={t('We could not read your usage just now.')}>
          <p>{t('Nothing is wrong with your app. Try again in a moment.')}</p>
        </Notice>
      </div>
    );
  }

  const base = project ? `/p/${encodeURIComponent(project.slug)}` : '';
  const metrics = forceState(usage.metrics, state);
  const resets = daysUntil(usage.resetsAt);

  // Wedding RSVP on the day it was made: three tracks at zero and, without
  // this branch, not one word about why. Three bars all reading "No traffic
  // yet" is a measurement of nothing presented as if it were a reading, and
  // the person it happens to is the person least able to tell a quiet app
  // from a broken one. So the zero case gets a sentence and the one thing
  // worth doing about it, and the bars stay underneath because the limits are
  // still the honest content of this screen. (Soi Cats used to be the example
  // here, until the Traffic screen's staged variant gave it a real request
  // total; the paused project carries the zero case now.)
  const noTraffic = metrics.every((metric) => metric.used <= 0);
  const address = project ? environmentOf(project).webAddress : null;
  const hasProjects = cachedProjects.length > 0;

  // Only one row may spend the screen's one ink block, and it is the first row
  // that has actually reached its limit. A page with three solid buttons on it
  // has no primary action at all.
  const inkId = metrics.find((metric) => tone(metric) === 'at' || tone(metric) === 'over')?.resource;

  // Account scope has nowhere to send anybody: there is no one database and
  // no one file browser behind a roll-up of every project.
  const destination: Partial<Record<UsageMetric['resource'], string>> = project
    ? {
        'database-storage': `${base}/database`,
        'file-storage': `${base}/files`,
      }
    : {};

  return (
    <div className="page">
      <header className="page__head">
        <h1 className="page__title">{t('Usage')}</h1>
        <p className="page__lede">
          {resets === 0
            ? t('Plan · resets today')
            : resets === 1
              ? t('Plan · resets tomorrow')
              : t('Plan · resets in {n} days', { n: resets })}
        </p>
      </header>

      <SectionPanel title={accountScope ? t('Account usage') : t('This project')}>
        {/* Three empty states, one condition each, because the true sentence
            is different in each case: an account with nothing in it, an
            account whose apps have not been visited, and one quiet app. All
            three keep the bars underneath, since the limits are the honest
            content of this screen whatever the numbers say. */}
        {noTraffic && accountScope && !hasProjects ? (
          <EmptyState
            mark="chart"
            sentence={t('You have not used anything yet.')}
            action={
              <Link to="/new">
                <Button
                  variant="solid"
                  onClick={() => captureEvent('usage_empty_new_project_clicked')}
                >
                  {t('New project')}
                </Button>
              </Link>
            }
          />
        ) : noTraffic && accountScope ? (
          <EmptyState
            mark="chart"
            sentence={t('Nobody has visited your apps yet.')}
            context={t('They are running. These are the limits they have room for.')}
            action={
              <Link to="/projects">
                <Button variant="solid">{t('See your apps')}</Button>
              </Link>
            }
          />
        ) : noTraffic && address ? (
          <EmptyState
            mark="chart"
            sentence={t('Nobody has visited your app yet.')}
            context={t('It is running. These are the limits it has room for.')}
            action={
              <Button
                variant="solid"
                onClick={() => window.open(address, '_blank', 'noopener,noreferrer')}
              >
                {t('Visit your app')}
              </Button>
            }
          />
        ) : null}

        <div className="uz">
          {metrics.map((metric) => (
            <UsageRow
              key={metric.resource}
              metric={metric}
              to={destination[metric.resource] ?? null}
              ink={inkId === metric.resource}
              limitAtZero={accountScope && !hasProjects}
            />
          ))}
        </div>
      </SectionPanel>

      <SectionPanel
        title={accountScope ? t('Plan and credits') : t('Plan')}
        note={
          accountScope
            ? t('One allowance for your whole account, shared by all of your apps.')
            : t('This app spends from your account allowance, shared with your other apps.')
        }
      >
        <PlanSummary usage={usage} />
      </SectionPanel>
    </div>
  );
}
